import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export interface Booking {
  _id: string;
  id: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  service: string;
  appointmentTime: string;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed' | 'no-show';
  createdAt: string;
  calendarEventId?: string;
}

interface BookingsResponse {
  bookings: Booking[];
}

interface CancelBookingParams {
  agentHandle: string;
  bookingId: string;
  reason?: string;
  source: 'customer' | 'manual' | 'system';
}

interface UpdateStatusParams {
  agentHandle: string;
  bookingId: string;
}

// Query keys factory
const bookingsKeys = {
  all: ['bookings'] as const,
  lists: () => [...bookingsKeys.all, 'list'] as const,
  list: (agentHandle: string, status?: string) =>
    [...bookingsKeys.lists(), agentHandle, status || 'all'] as const,
};

// Fetch bookings
export const useBookings = (agentHandle: string, filterStatus: string = 'all') => {
  return useQuery<BookingsResponse>({
    queryKey: bookingsKeys.list(agentHandle, filterStatus),
    queryFn: async () => {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No auth token');

      const statusParam = filterStatus !== 'all' ? `?status=${filterStatus}` : '';
      const response = await fetch(
        `${API_BASE}/api/booking-management/${agentHandle}/bookings${statusParam}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch bookings');
      }

      return response.json();
    },
    enabled: !!agentHandle,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 2 * 60 * 1000, // Auto-refetch every 2 minutes
  });
};

// Cancel booking mutation
export const useCancelBooking = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ agentHandle, bookingId, reason, source }: CancelBookingParams) => {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No auth token');

      const response = await fetch(
        `${API_BASE}/api/booking-management/${agentHandle}/booking/${bookingId}/mark-cancelled`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            reason: reason?.trim() || null,
            source
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to cancel appointment');
      }

      return response.json();
    },
    onSuccess: (_, variables) => {
      // Invalidate all booking queries for this agent
      queryClient.invalidateQueries({
        queryKey: bookingsKeys.lists()
      });
    },
  });
};

// Mark as completed mutation
export const useMarkCompleted = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ agentHandle, bookingId }: UpdateStatusParams) => {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No auth token');

      const response = await fetch(
        `${API_BASE}/api/booking-management/${agentHandle}/booking/${bookingId}/mark-completed`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to mark as completed');
      }

      return response.json();
    },
    onMutate: async ({ agentHandle, bookingId }) => {
      // Optimistic update: Update the booking status immediately
      const queryKey = bookingsKeys.lists();

      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot previous value
      const previousBookings = queryClient.getQueriesData({ queryKey });

      // Optimistically update to the new value
      queryClient.setQueriesData({ queryKey }, (old: any) => {
        if (!old?.bookings) return old;
        return {
          ...old,
          bookings: old.bookings.map((booking: Booking) =>
            booking.id === bookingId ? { ...booking, status: 'completed' as const } : booking
          ),
        };
      });

      return { previousBookings };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousBookings) {
        context.previousBookings.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      // Refetch to ensure data is in sync
      queryClient.invalidateQueries({ queryKey: bookingsKeys.lists() });
    },
  });
};

// Mark as no-show mutation
export const useMarkNoShow = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ agentHandle, bookingId }: UpdateStatusParams) => {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No auth token');

      const response = await fetch(
        `${API_BASE}/api/booking-management/${agentHandle}/booking/${bookingId}/mark-no-show`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to mark as no-show');
      }

      return response.json();
    },
    onMutate: async ({ agentHandle, bookingId }) => {
      // Optimistic update
      const queryKey = bookingsKeys.lists();
      await queryClient.cancelQueries({ queryKey });

      const previousBookings = queryClient.getQueriesData({ queryKey });

      queryClient.setQueriesData({ queryKey }, (old: any) => {
        if (!old?.bookings) return old;
        return {
          ...old,
          bookings: old.bookings.map((booking: Booking) =>
            booking.id === bookingId ? { ...booking, status: 'no-show' as const } : booking
          ),
        };
      });

      return { previousBookings };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousBookings) {
        context.previousBookings.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: bookingsKeys.lists() });
    },
  });
};

// Approve booking mutation
export const useApproveBooking = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ agentHandle, bookingId }: UpdateStatusParams) => {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No auth token');

      const response = await fetch(
        `${API_BASE}/api/booking-management/${agentHandle}/booking/${bookingId}/approve`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to approve booking');
      }

      return response.json();
    },
    onMutate: async ({ agentHandle, bookingId }) => {
      // Optimistic update
      const queryKey = bookingsKeys.lists();
      await queryClient.cancelQueries({ queryKey });

      const previousBookings = queryClient.getQueriesData({ queryKey });

      queryClient.setQueriesData({ queryKey }, (old: any) => {
        if (!old?.bookings) return old;
        return {
          ...old,
          bookings: old.bookings.map((booking: Booking) =>
            booking.id === bookingId ? { ...booking, status: 'confirmed' as const } : booking
          ),
        };
      });

      return { previousBookings };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousBookings) {
        context.previousBookings.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: bookingsKeys.lists() });
    },
  });
};

// Reject booking mutation
export const useRejectBooking = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ agentHandle, bookingId, reason }: { agentHandle: string; bookingId: string; reason?: string }) => {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No auth token');

      const response = await fetch(
        `${API_BASE}/api/booking-management/${agentHandle}/booking/${bookingId}/reject`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ reason: reason?.trim() || null })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to reject booking');
      }

      return response.json();
    },
    onMutate: async ({ agentHandle, bookingId }) => {
      // Optimistic update
      const queryKey = bookingsKeys.lists();
      await queryClient.cancelQueries({ queryKey });

      const previousBookings = queryClient.getQueriesData({ queryKey });

      queryClient.setQueriesData({ queryKey }, (old: any) => {
        if (!old?.bookings) return old;
        return {
          ...old,
          bookings: old.bookings.map((booking: Booking) =>
            booking.id === bookingId ? { ...booking, status: 'cancelled' as const } : booking
          ),
        };
      });

      return { previousBookings };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousBookings) {
        context.previousBookings.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: bookingsKeys.lists() });
    },
  });
};
